import React, { createContext, useContext, useEffect } from 'react';
import moment from 'moment';

import { useSiteTime } from '@app/features/store-core';
import { useGetGoals, useGetWeeklyFocusSettings } from '../hooks';

interface GoalSettingsContextReturn {
  goals: ReturnType<typeof useGetGoals>[0];
  weeklyFocusSettings: ReturnType<typeof useGetWeeklyFocusSettings>[0];
}

type GoalSettingsProviderProps = {
  children: React.ReactNode;
  brandId?: string;
  customerId?: string;
  marketId?: number;
  siteId?: string;
  weekStartDate?: Date;
};

const GoalSettingsContext = createContext<GoalSettingsContextReturn>(undefined!);

export const GoalSettingsProvider = ({ children, brandId, customerId, marketId, weekStartDate }: GoalSettingsProviderProps) => {
  const siteTime = useSiteTime();

  const [weeklyFocusSettings, setWeeklyFocusSettingsParams] = useGetWeeklyFocusSettings();
  const [goals, setGoalsParams] = useGetGoals();

  useEffect(() => {
    if (brandId && customerId) {
      const startOfWeek = siteTime.toStartEndOfWeekBlock().startTime;
      const date = weekStartDate ? moment(weekStartDate).format().slice(0, 10) : undefined;

      setWeeklyFocusSettingsParams({ brandId, customerId, marketId, date });
      setGoalsParams({ brandId, customerId, marketId });
    }
  }, [brandId, customerId, marketId, weekStartDate]);

  return <GoalSettingsContext.Provider value={{ goals, weeklyFocusSettings }}>{children}</GoalSettingsContext.Provider>;
};

export const GoalSettingsConsumer = GoalSettingsContext.Consumer;

export const useGoalSettings = () => {
  // get the context
  const context = useContext(GoalSettingsContext);

  // if `undefined`, throw an error
  if (context === undefined) {
    throw new Error('useGoalSettings was used outside of its Provider');
  }
  return context;
};
